import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Users, TrendingUp, CreditCard, BarChart3, Activity,
  PieChart, ArrowUpRight, ArrowDownRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const signupChartRef = useRef(null);
  const optChartRef = useRef(null);
  const revenueChartRef = useRef(null);
  const planChartRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/analytics/admin/platform?days=${days}`);
        setData(res.data);
      } catch { /* auth redirect */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [days]);

  // Draw charts when data changes
  useEffect(() => {
    if (!data) return;
    drawLineChart(signupChartRef.current, data.daily_signups, "count", "#6366f1");
    drawLineChart(optChartRef.current, data.daily_optimizations, "count", "#8b5cf6");
    drawRevenueChart(revenueChartRef.current, data.daily_revenue);
    drawPieChart(planChartRef.current, data.plan_distribution);
  }, [data]);

  if (loading && !data) {
    return <div className="analytics-loading" data-testid="admin-analytics-loading"><div className="processing-spinner" /></div>;
  }

  if (!data) {
    return <div className="analytics-loading">Failed to load analytics</div>;
  }

  const { users, optimizations, revenue } = data;

  return (
    <div className="page-container fade-in" data-testid="admin-analytics-page">
      <div className="page-header">
        <div>
          <h1 data-testid="admin-analytics-title">Platform Analytics</h1>
          <p className="page-subtitle">User growth, revenue, and engagement metrics.</p>
        </div>
        <div className="analytics-period-select" data-testid="period-selector">
          {[7, 14, 30, 60, 90].map(d => (
            <button
              key={d}
              className={`analytics-period-btn ${days === d ? "active" : ""}`}
              onClick={() => setDays(d)}
              data-testid={`period-${d}d`}
            >{d}d</button>
          ))}
        </div>
      </div>

      {/* KPI Hero Cards */}
      <div className="analytics-kpi-grid" data-testid="admin-kpi-cards">
        <div className="analytics-kpi-card" data-testid="kpi-total-users">
          <div className="analytics-kpi-top">
            <Users size={18} />
            <span className="analytics-kpi-label">Total Users</span>
          </div>
          <div className="analytics-kpi-value">{users.total}</div>
          <div className="analytics-kpi-sub">
            <span className="analytics-kpi-delta positive">
              <ArrowUpRight size={12} /> +{users.new_in_period}
            </span>
            <span>in {days}d</span>
          </div>
        </div>

        <div className="analytics-kpi-card" data-testid="kpi-total-revenue">
          <div className="analytics-kpi-top">
            <CreditCard size={18} />
            <span className="analytics-kpi-label">Total Revenue</span>
          </div>
          <div className="analytics-kpi-value">{revenue.total_display}</div>
          <div className="analytics-kpi-sub">
            <span className="analytics-kpi-delta positive">
              {revenue.period_display}
            </span>
            <span>in {days}d</span>
          </div>
        </div>

        <div className="analytics-kpi-card" data-testid="kpi-total-optimizations">
          <div className="analytics-kpi-top">
            <BarChart3 size={18} />
            <span className="analytics-kpi-label">Optimizations</span>
          </div>
          <div className="analytics-kpi-value">{optimizations.total}</div>
          <div className="analytics-kpi-sub">
            <span className="analytics-kpi-delta positive">
              <ArrowUpRight size={12} /> +{optimizations.in_period}
            </span>
            <span>in {days}d</span>
          </div>
        </div>

        <div className="analytics-kpi-card" data-testid="kpi-conversion-rate">
          <div className="analytics-kpi-top">
            <TrendingUp size={18} />
            <span className="analytics-kpi-label">Conversion Rate</span>
          </div>
          <div className="analytics-kpi-value">{users.conversion_rate}%</div>
          <div className="analytics-kpi-sub">
            <span>Trial to Paid</span>
          </div>
        </div>
      </div>

      {/* Plan Distribution */}
      <div className="analytics-row" data-testid="analytics-row-plans">
        <div className="analytics-chart-section analytics-chart-narrow" data-testid="plan-distribution-section">
          <div className="analytics-chart-header">
            <h2><PieChart size={18} /> Plan Distribution</h2>
          </div>
          <div className="analytics-chart-wrap analytics-chart-small" data-testid="plan-chart">
            <canvas ref={planChartRef} className="analytics-canvas" />
          </div>
          <div className="analytics-plan-legend" data-testid="plan-legend">
            {data.plan_distribution.filter(p => p.count > 0).map((p, i) => (
              <div key={p.plan} className="analytics-legend-item">
                <span className="analytics-legend-dot" style={{ background: PLAN_COLORS[i % PLAN_COLORS.length] }} />
                <span className="analytics-legend-label">{p.plan}</span>
                <span className="analytics-legend-count">{p.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* User Breakdown */}
        <div className="analytics-chart-section analytics-chart-wide" data-testid="user-breakdown-section">
          <div className="analytics-chart-header">
            <h2><Users size={18} /> User Breakdown</h2>
          </div>
          <div className="analytics-breakdown-grid" data-testid="user-breakdown">
            <BreakdownCard label="Trial" value={users.trial} color="var(--text-muted)" />
            <BreakdownCard label="Base" value={users.base} color="var(--primary)" />
            <BreakdownCard label="Unlimited" value={users.unlimited} color="var(--success)" />
            <BreakdownCard label="No Plan" value={users.no_plan} color="var(--warning)" />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="analytics-charts-two-col" data-testid="analytics-charts">
        <div className="analytics-chart-section" data-testid="signup-chart-section">
          <div className="analytics-chart-header">
            <h2><Users size={18} /> Daily Signups</h2>
          </div>
          <div className="analytics-chart-wrap" data-testid="signup-chart">
            <canvas ref={signupChartRef} className="analytics-canvas" />
          </div>
        </div>

        <div className="analytics-chart-section" data-testid="optimization-chart-section">
          <div className="analytics-chart-header">
            <h2><Activity size={18} /> Daily Optimizations</h2>
          </div>
          <div className="analytics-chart-wrap" data-testid="optimization-chart">
            <canvas ref={optChartRef} className="analytics-canvas" />
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="analytics-chart-section" data-testid="revenue-chart-section">
        <div className="analytics-chart-header">
          <h2><CreditCard size={18} /> Daily Revenue</h2>
        </div>
        <div className="analytics-chart-wrap" data-testid="revenue-chart">
          <canvas ref={revenueChartRef} className="analytics-canvas" />
        </div>
      </div>
    </div>
  );
}

const PLAN_COLORS = ["#6366f1", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];

function BreakdownCard({ label, value, color }) {
  return (
    <div className="analytics-breakdown-item">
      <div className="analytics-breakdown-dot" style={{ background: color }} />
      <div>
        <div className="analytics-breakdown-value">{value}</div>
        <div className="analytics-breakdown-label">{label}</div>
      </div>
    </div>
  );
}

// ── Chart helpers ──
function getChartStyles() {
  const isDark = getComputedStyle(document.documentElement).getPropertyValue("--bg-deep").trim().startsWith("#0");
  return {
    gridColor: isDark ? "rgba(148,163,184,0.08)" : "rgba(15,23,42,0.06)",
    textColor: isDark ? "#64748b" : "#94a3b8",
  };
}

function drawLineChart(canvas, data, valueKey, color) {
  if (!canvas || !data || data.length === 0) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);
  const w = rect.width;
  const h = rect.height;
  const pad = { top: 16, right: 12, bottom: 32, left: 40 };
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const { gridColor, textColor } = getChartStyles();

  ctx.clearRect(0, 0, w, h);

  const values = data.map(d => d[valueKey] || 0);
  const maxVal = Math.max(...values, 1);

  // Y gridlines
  for (let i = 0; i <= 4; i++) {
    const val = Math.round((maxVal / 4) * i);
    const y = pad.top + chartH - (val / maxVal) * chartH;
    ctx.fillStyle = textColor;
    ctx.font = "10px Inter, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(val.toString(), pad.left - 6, y + 3);
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(w - pad.right, y);
    ctx.stroke();
  }

  if (data.length < 2) return;
  const step = chartW / (data.length - 1);

  // Gradient fill
  const gradient = ctx.createLinearGradient(0, pad.top, 0, pad.top + chartH);
  gradient.addColorStop(0, color + "30");
  gradient.addColorStop(1, color + "00");

  ctx.beginPath();
  data.forEach((d, i) => {
    const x = pad.left + i * step;
    const y = pad.top + chartH - ((d[valueKey] || 0) / maxVal) * chartH;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.lineTo(pad.left + (data.length - 1) * step, pad.top + chartH);
  ctx.lineTo(pad.left, pad.top + chartH);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();

  // Line
  ctx.beginPath();
  data.forEach((d, i) => {
    const x = pad.left + i * step;
    const y = pad.top + chartH - ((d[valueKey] || 0) / maxVal) * chartH;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineJoin = "round";
  ctx.stroke();

  // X labels
  const showEvery = Math.ceil(data.length / 6);
  data.forEach((d, i) => {
    if (i % showEvery === 0 || i === data.length - 1) {
      const x = pad.left + i * step;
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.font = "9px Inter, sans-serif";
      const label = (d.date || "").slice(5);
      ctx.fillText(label, x, h - 8);
    }
  });
}

function drawRevenueChart(canvas, data) {
  if (!canvas || !data || data.length === 0) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);
  const w = rect.width;
  const h = rect.height;
  const pad = { top: 16, right: 12, bottom: 32, left: 50 };
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const { gridColor, textColor } = getChartStyles();

  ctx.clearRect(0, 0, w, h);

  const values = data.map(d => d.amount_paise || 0);
  const maxVal = Math.max(...values, 100);

  // Y gridlines (in rupees)
  for (let i = 0; i <= 4; i++) {
    const val = Math.round((maxVal / 4) * i);
    const y = pad.top + chartH - (val / maxVal) * chartH;
    ctx.fillStyle = textColor;
    ctx.font = "10px Inter, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(`₹${Math.round(val / 100)}`, pad.left - 6, y + 3);
    ctx.strokeStyle = gridColor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(pad.left, y);
    ctx.lineTo(w - pad.right, y);
    ctx.stroke();
  }

  // Bars
  const barGap = Math.max(1, Math.floor(chartW / data.length * 0.2));
  const barW = Math.max(2, (chartW / data.length) - barGap);

  data.forEach((d, i) => {
    const val = d.amount_paise || 0;
    const barH = maxVal > 0 ? (val / maxVal) * chartH : 0;
    const x = pad.left + i * (barW + barGap) + barGap / 2;
    const y = pad.top + chartH - barH;

    const radius = Math.min(3, barW / 2);
    ctx.beginPath();
    ctx.moveTo(x, pad.top + chartH);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.lineTo(x + barW - radius, y);
    ctx.quadraticCurveTo(x + barW, y, x + barW, y + radius);
    ctx.lineTo(x + barW, pad.top + chartH);
    ctx.closePath();
    ctx.fillStyle = val > 0 ? "#10b981" : "rgba(148,163,184,0.1)";
    ctx.fill();

    const showEvery = Math.ceil(data.length / 8);
    if (i % showEvery === 0 || i === data.length - 1) {
      ctx.fillStyle = textColor;
      ctx.textAlign = "center";
      ctx.font = "9px Inter, sans-serif";
      ctx.fillText((d.date || "").slice(5), x + barW / 2, h - 8);
    }
  });
}

function drawPieChart(canvas, distribution) {
  if (!canvas || !distribution) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.scale(dpr, dpr);
  const w = rect.width;
  const h = rect.height;

  ctx.clearRect(0, 0, w, h);

  const total = distribution.reduce((s, d) => s + d.count, 0);
  if (total === 0) {
    ctx.fillStyle = "#64748b";
    ctx.font = "12px Inter, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("No data", w / 2, h / 2);
    return;
  }

  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) / 2 - 12;
  const innerR = r * 0.55;
  let startAngle = -Math.PI / 2;

  distribution.forEach((d, i) => {
    if (d.count === 0) return;
    const sliceAngle = (d.count / total) * 2 * Math.PI;
    ctx.beginPath();
    ctx.arc(cx, cy, r, startAngle, startAngle + sliceAngle);
    ctx.arc(cx, cy, innerR, startAngle + sliceAngle, startAngle, true);
    ctx.closePath();
    ctx.fillStyle = PLAN_COLORS[i % PLAN_COLORS.length];
    ctx.fill();
    startAngle += sliceAngle;
  });

  // Center text
  ctx.fillStyle = getChartStyles().textColor;
  ctx.font = "bold 16px Outfit, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(total.toString(), cx, cy - 6);
  ctx.font = "10px Inter, sans-serif";
  ctx.fillText("users", cx, cy + 10);
}
